'use client'
import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { Breadcrumbs } from 'src/components/Breadcrumbs'
import DOMPurify from 'src/_helpers/dompurify'
import { getMediaUrl } from 'src/_helpers/client/getMediaUrl'
import Typography from 'src/components/design-system/Typography'
import SocialShare from 'src/components/design-system/SocialShare'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import Tabs from 'src/components/design-system/Tabs'
import { gsap } from 'gsap'
import LINKS from 'src/_helpers/links'
import useJournalDomainRoutes from 'src/components/journals/domain/useJournalDomainRoutes'
import { buildLocalizedPath } from 'src/_helpers/localizedLinks'
import Link from 'next/link'
import Accordion from 'src/components/design-system/Acordion'
import Keywords from 'src/components/journals/components/Keywords'
import {
  joinTranslatedTitles,
  JournalInfoGrid,
  JournalIssueAside,
  JournalLicenseLinks,
  JournalLicenseModels,
} from 'src/components/journals/components/JournalHeaderShared'
// icon
import Star from 'public/icons/interface/star.svg'
import Arrow from 'public/icons/arrows/arrow-right.svg'
import Accessibility from 'public/icons/interface/accessibility.svg'
import { getPreferredTranslation } from 'src/_helpers/getPreferredTranslation'
import { hasRichContent } from 'src/_helpers/hasRichContent'
import EditorialPanelEntryButtons from 'src/components/journals/components/EditorialPanelEntryButtons'
import { getHostedSiteUrl } from 'src/_helpers/getExternalSiteUrl'
import { DoiInfoLink } from 'src/components/publications/PublicationHeaderShared'
import {
  buildIssueLabel,
  buildPrefixedLabel,
  buildVolumeTitle,
} from 'src/components/journals/issues/list/helpers/journalIssuesListUtils'
// end icon
const htmlTagRegex = /<[a-z][\s\S]*>/i

const sanitizeRichText = (value) => {
  if (!value) return null

  const sanitized = DOMPurify.sanitize(value)

  return htmlTagRegex.test(value) ? sanitized : sanitized.replace(/\n/g, '<br />')
}

const Header = ({ page, breadcrumbs, locale, variant = '', routeContext = 'main' }) => {
  const translation = getPreferredTranslation(page, locale) || {}

  const translate = useTranslateEntry()
  const journalRoutes = useJournalDomainRoutes()
  const [isActive, setIsActive] = useState('info')
  const infoRef = useRef(null)
  const descRef = useRef(null)
  const tags = translation?.tags || []
  const title = translation?.title
  const subtitle = page.number ? `${translate('T_NUMBER')} ${page.number}` : ''
  // Numer bez własnego ISSN/eISSN pokazuje wartości czasopisma, chyba że ma identyfikatory
  // niezależne od czasopisma — wtedy puste pole jest pomijane.
  const isNumber = variant === 'number'
  const ownIdentifiers = isNumber && page?.ownIdentifiers === true
  const issnSource = isNumber ? page?.journal : page
  const issn = page?.issn || (ownIdentifiers ? null : issnSource?.issn)
  const eIssn = page?.eIssn || (ownIdentifiers ? null : issnSource?.eIssn)
  const isbn = isNumber ? page?.isbn : null
  const issnItems = [issn && <p key="issn">ISSN: {issn}</p>, eIssn && <p key="eissn">eISSN: {eIssn}</p>].filter(Boolean)
  const doi = page.doi
  const doiUrl = page.doiUrl
  const doiPrefixes = Array.isArray(page.doiPrefixes) ? page.doiPrefixes.filter(Boolean) : []
  const releaseYear = page.releaseYear
  const periodicity = translation?.periodicity
  const journalTranslation = page?.journal ? getPreferredTranslation(page?.journal, locale) : {}
  const journalTitle = journalTranslation?.title
  const descHtml = sanitizeRichText(translation?.description)
  const desc = hasRichContent(descHtml) ? descHtml : null
  const descriptionTitle = translation?.descriptionTitle
  const descriptionTabTitle =
    translation?.descriptionTabTitle || journalTranslation?.descriptionTabTitle || translate('T_DESCRIPTION')
  // Linki redakcji (JournalLink), a za nimi systemowe, np. „Aktualności” przy co najmniej jednej aktualności.
  const linksOpen = [
    ...(page.linksOpen || []).map((link) => {
      const translation = getPreferredTranslation(link, locale) || {}

      return { title: translation.title, url: translation.link }
    }),
    ...(page.systemLinksOpen || []),
  ].filter((link) => link.title && link.url)
  const isDomainJournal = routeContext === 'journal-domain'
  // Czasopismo z hostingiem ma na publisphere tylko wizytówkę z linkiem do własnej domeny.
  const hostedSiteUrl = routeContext === 'main' && variant !== 'number' ? getHostedSiteUrl(page) : null
  const isBusinessCard = !!hostedSiteUrl
  const routeMaps = isDomainJournal ? journalRoutes : null
  const menuItems = isDomainJournal && Array.isArray(page.menu) ? page.menu : []
  const licencesModels = page.licencesModels
  const licences = page.licenses
  const disciplines = page.disciplines
  const fields = page.fields
  const publicationLanguages = page.publicationLanguages
  const publishers = Array.isArray(page?.publishers) ? page.publishers : []
  const publishersLabel = [joinTranslatedTitles(publishers, locale), isNumber ? page?.publisherNote : null]
    .filter(Boolean)
    .join(', ')
  const affiliations = Array.isArray(page.affiliations) && page.affiliations.length > 0 ? page.affiliations : null
  const editorInChiefItems = Array.isArray(page.editorInChiefs)
    ? page.editorInChiefs
    : page.editorInChief
      ? [page.editorInChief]
      : []
  const guestEditorItems = Array.isArray(page.guestEditors)
    ? page.guestEditors
    : page.guestEditor
      ? [page.guestEditor]
      : []
  const formatEditorName = (editor) => {
    const editorTranslation = getPreferredTranslation(editor, locale) || {}

    return editor?.fullName ? `${editorTranslation?.academicPrefix || ''} ${editor.fullName}`.trim() : null
  }
  const getMenuItemLink = (link) => {
    if (link?.stat === false) {
      return null
    }

    const itemTranslation = getPreferredTranslation(link, locale) || {}
    const pageTranslations = link?.pageData?.translations || {}
    const pageTranslation =
      pageTranslations[locale] || pageTranslations.pl || pageTranslations.en || Object.values(pageTranslations)[0] || {}
    const label = itemTranslation.title || pageTranslation.title
    const href =
      itemTranslation.link ||
      (pageTranslation.slug
        ? buildLocalizedPath(routeMaps?.JOURNALS.SUBPAGE || LINKS.JOURNALS.SUBPAGE, locale, {
            journal: translation?.slug,
            slug: pageTranslation.slug,
          })
        : null)

    return label && href ? { label, href } : null
  }
  const editorInChiefNames = editorInChiefItems.map(formatEditorName).filter(Boolean)
  const guestEditorNames = guestEditorItems.map(formatEditorName).filter(Boolean)
  const editorInChief = editorInChiefNames.join(', ')
  const guestEditor = guestEditorNames.join(', ')
  const affiliation = page.affiliations?.[0] || null
  const affiliationTranslate = getPreferredTranslation(affiliation, locale) || {}
  const affiliationTitle = affiliationTranslate?.title
  const affiliationAlt = affiliationTranslate?.alt || affiliationTranslate?.title
  const affiliationThumb = affiliation?.thumbs?.journal
  const journalLogoThumb = page?.logo1 || page?.thumbs?.logo
  const headerLogo = affiliationThumb || journalLogoThumb
  const headerLogoAlt = affiliationThumb ? affiliationAlt : title
  const headerLogoUrl = headerLogo ? getMediaUrl(headerLogo.url, headerLogo.version) : null
  const translationNewestVolume = getPreferredTranslation(page.newestVolume, locale) || {}
  const newestVolumeTitle = translationNewestVolume?.title
  const currentVolumeTitle = buildPrefixedLabel(
    buildVolumeTitle(page.newestVolume, locale, translate('T_VOLUME_PREFIX')),
    translate('T_VOLUME_PREFIX'),
  )
  const fallbackIssueTitle = buildIssueLabel(
    page,
    locale,
    translate('T_ISSUE_PREFIX') || translate('T_NUMBER'),
    currentVolumeTitle,
    translate('T_UNTITLED'),
  )
  const currentTitle = variant === 'number' ? translation?.title || page?.title || fallbackIssueTitle : title
  const translationIssue = (page.newestIssue ? getPreferredTranslation(page.newestIssue, locale) : null) || {}
  //const translationIssue = page.newestIssue?.translations?.[locale] || {}
  const alt =
    variant === 'number' ? translation.alt || translation.title : translationIssue.alt || translationIssue.title
  const thumb = variant === 'number' ? page?.thumbs?.detail : page.newestIssue?.thumbs?.detail
  const thumbUrl = thumb ? getMediaUrl(thumb.url, thumb.version) : null
  const thumbBig = variant === 'number' ? page?.thumbs?.fullscreen : page.newestIssue?.thumbs?.fullscreen
  const thumbBigUrl = thumbBig ? getMediaUrl(thumbBig.url, thumbBig.version) : null
  const titleIssue = page?.newestIssue?.number
    ? `${translate('T_NUMBER')} ${page.newestIssue.number}`
    : translationIssue.title
  const subtitleIssue = translationIssue.subtitle
  const yearIssue = page.newestIssue?.releaseYear
  const urlIssue = buildLocalizedPath(routeMaps?.ISSUES.DETAIL || LINKS.ISSUES.DETAIL, locale, {
    slug: translation?.slug,
    issue: translationIssue?.slug,
  })
  const urlIssues = buildLocalizedPath(routeMaps?.ISSUES.LIST || LINKS.ISSUES.LIST, locale, { slug: translation?.slug })
  useEffect(() => {
    const activeEl = isActive === 'info' ? infoRef.current : descRef.current

    gsap.fromTo(activeEl, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.4, ease: 'power3.out' })
  }, [isActive])
  const tableOfContentsUrl = page.tableOfContents?.url || null
  const infoItems = [
    ...(variant === 'number' && page.special
      ? [
          {
            type: 'custom',
            content: (
              <p className="col-span-full flex items-center gap-x-10">
                <Star className="w-19 h-18" />
                <Typography variant="h4-caps-bold" component="span" color="text-basic-black">
                  {translate('T_SPECIAL_NUMBER')}
                </Typography>
              </p>
            ),
          },
        ]
      : []),
    ...(variant === 'number' && issnItems.length > 0 ? [{ label: 'ISSN / eISSN', content: issnItems }] : []),
    ...(isbn ? [{ label: 'ISBN', content: isbn }] : []),
    ...(variant === 'number' ? [{ type: 'divider' }] : []),
    ...(variant === 'number'
      ? [{ label: translate('T_JOURNAL'), content: journalTitle }]
      : issnItems.length > 0
        ? [{ label: 'ISSN / eISSN', content: issnItems }]
        : []),
    {
      label: 'DOI',
      content: doi ? <DoiInfoLink doi={doi} href={doiUrl} fallback={doi} /> : null,
    },
    ...(doiPrefixes.length > 0
      ? [
          {
            label: 'DOI',
            content: doiPrefixes.map((prefix, prefixIndex) => <p key={`doi-prefix-${prefixIndex}`}>{prefix}</p>),
          },
        ]
      : []),
    { type: 'divider' },
    {
      label: translate('T_LICENSE_MODEL'),
      content: joinTranslatedTitles(licencesModels, locale) ? (
        <JournalLicenseModels items={licencesModels} locale={locale} />
      ) : null,
    },
    {
      label: translate('T_LICENSE_TYPE'),
      content: joinTranslatedTitles(licences, locale) ? (
        <JournalLicenseLinks
          items={licences}
          locale={locale}
          linkClassName="block w-fit h-fit relative after:content-[''] after:h-1 after:w-full after:transition-[width] after:absolute after:bottom-0 after:left-0 hover:after:w-0 after:bg-[currentColor]"
        />
      ) : null,
    },
    { type: 'divider' },
    { label: translate('T_FIELD'), content: joinTranslatedTitles(fields, locale) },
    { label: translate('T_DISCIPLINE'), content: joinTranslatedTitles(disciplines, locale) },
    { label: translate('T_PUBLISHER'), content: publishersLabel, className: 'd:col-1' },
    { label: translate('T_AFFILIATING_INSTITUTION'), content: joinTranslatedTitles(affiliations, locale) },
    { type: 'divider' },
    {
      label: translate('T_LANGUAGE_OF_PUBLICATION'),
      content: joinTranslatedTitles(publicationLanguages, locale),
    },
    {
      label: translate(variant === 'number' ? 'T_RELEASE_YEAR' : 'T_JOURNAL_RELEASE_YEAR'),
      content: releaseYear,
    },
    {
      label: translate(editorInChiefNames.length > 1 ? 'T_EDITORS_IN_CHIEF' : 'T_EDITOR_IN_CHIEF'),
      content: editorInChief,
      className: 'd:col-1',
    },
    {
      label:
        variant === 'number'
          ? translate(guestEditorNames.length > 1 ? 'T_GUEST_EDITORS' : 'T_GUEST_EDITOR')
          : translate('T_PUBLICATION_FREQUENCY'),
      content: variant === 'number' ? guestEditor : periodicity,
    },
    ...(page.meetsAccessibilityRequirements
      ? [
          {
            type: 'custom',
            content: (
              <div className="flex items-center gap-10">
                <Accessibility />
                <Typography variant="h3-caps-bold" component="h2" color="text-basic-black">
                  {translate('T_ACCESSIBILITY_INFO')}
                </Typography>
              </div>
            ),
          },
        ]
      : []),
  ]

  return (
    <header
      className={`flex flex-col gap-y-64 d:px-40 px-16 ${menuItems.length === 0 && tags.length === 0 ? 'mb-64' : ''}`}
    >
      <Breadcrumbs breadcrumbs={breadcrumbs} current={currentTitle} />
      <div className="flex flex-col gap-y-32">
        <div className="flex items-end justify-between flex-col d:flex-row gap-y-11">
          <div className="flex flex-col gap-y-24">
            <Typography variant="h1-bold" color="text-basic-black max-w-665 block">
              {subtitle ? subtitle : title}
            </Typography>
            {subtitle && title && (
              <Typography variant="h2" color="text-basic-black max-w-665 block">
                {subtitle ? title : ''}
              </Typography>
            )}
          </div>

          <SocialShare title={currentTitle} />
        </div>
        {variant !== 'number' && (
          <div
            className={`flex d:items-center justify-between py-15 border-y-1 ${
              isDomainJournal ? 'border-y-basic-gray8' : 'border-y-dark-blue-dark-blue6'
            } flex-col d:flex-row gap-y-16`}
          >
            <div className="flex d:items-center gap-x-16 flex-col d:flex-row gap-y-11">
              <hr
                className={`hidden d:block w-1 h-60 ${
                  isDomainJournal
                    ? 'bg-basic-gray8 text-basic-gray8'
                    : 'bg-dark-blue-dark-blue6 text-dark-blue-dark-blue6'
                }`}
              />
              {headerLogoUrl && (
                <div>
                  <Image
                    className="max-h-41 w-auto object-contain"
                    src={headerLogoUrl}
                    alt={headerLogoAlt || ''}
                    width={headerLogo.width}
                    height={headerLogo.height}
                  />
                </div>
              )}
              {affiliationTitle && headerLogoUrl && (
                <hr
                  className={`hidden d:block w-1 h-60 ${
                    isDomainJournal
                      ? 'bg-basic-gray8 text-basic-gray8'
                      : 'bg-dark-blue-dark-blue6 text-dark-blue-dark-blue60'
                  }`}
                />
              )}
              {affiliationTitle && (
                <Typography variant="body4" component="span" color="text-basic-black">
                  {affiliationTitle}
                </Typography>
              )}
            </div>
            <EditorialPanelEntryButtons journal={page} isDomainJournal={isDomainJournal} />
          </div>
        )}
      </div>
      <div className="grid d:grid-cols-[3fr_9fr] gap-40">
        <div className="flex flex-col gap-y-24">
          {((page.newestIssue && page.newestIssue.stat) || variant === 'number') && (
            <JournalIssueAside
              title={variant !== 'number' ? translate('T_NEWEST_ISSUE') : null}
              cover={thumbUrl}
              coverLarge={thumbBigUrl || thumbUrl}
              coverWidth={thumb?.width}
              coverHeight={thumb?.height}
              coverLargeWidth={thumbBig?.width || thumb?.width}
              coverLargeHeight={thumbBig?.height || thumb?.height}
              alt={alt}
              issueTitle={variant !== 'number' ? titleIssue : null}
              issueSubtitle={variant !== 'number' ? subtitleIssue : null}
              volumeTitle={variant !== 'number' ? newestVolumeTitle : null}
              year={variant !== 'number' ? yearIssue : null}
              issueHref={variant !== 'number' && !isBusinessCard ? urlIssue : null}
              issuesHref={variant !== 'number' && !isBusinessCard ? urlIssues : null}
              tableOfContentsHref={variant === 'number' && tableOfContentsUrl ? getMediaUrl(tableOfContentsUrl) : null}
              tableOfContentsLabel={translate('T_SEE_TABLE_OF_CONTENTS')}
              showAllIssues={variant !== 'number' && !isBusinessCard}
              color="platform"
              translate={translate}
            />
          )}
          {hostedSiteUrl && (
            <Link
              href={hostedSiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex w-fit items-center gap-x-6"
            >
              <Typography
                variant="button-bold"
                component="span"
                color="text-dark-blue-dark-blue2"
                className="block w-fit h-fit relative after:content-[''] after:h-1 after:w-0 after:transition-[width] after:absolute after:bottom-0 after:left-0 group-hover:after:w-full after:bg-dark-blue-dark-blue2"
              >
                {translate('T_JOURNAL_WEBSITE')}
              </Typography>
              <span className="w-18 h-18 flex items-center justify-center rounded-full border border-dark-blue-dark-blue2">
                <Arrow className="text-dark-blue-dark-blue2 -rotate-45" />
              </span>
            </Link>
          )}

          <hr
            className={`w-full h-1 ${
              isDomainJournal ? 'bg-basic-gray8 text-basic-gray8' : 'bg-dark-blue-dark-blue6 text-dark-blue-dark-blue6'
            }`}
          />
          {linksOpen.length > 0 && (
            <div className="flex flex-col gap-y-11">
              {linksOpen.map(({ title, url }, linkIndex) => (
                <Link href={url} key={`linkOpen-${linkIndex}`} className="group flex items-center gap-x-6">
                  <Typography
                    variant="button-bold"
                    component="span"
                    color={isDomainJournal ? 'text-basic-black' : 'text-dark-blue-dark-blue2'}
                    className={`block w-fit h-fit relative after:content-[''] after:h-1 after:w-0 after:transition-[width] after:absolute after:bottom-0 after:left-0 group-hover:after:w-full ${
                      isDomainJournal ? 'after:bg-basic-black' : 'after:bg-dark-blue-dark-blue2'
                    }`}
                  >
                    {title}
                  </Typography>

                  <span
                    className={`w-18 h-18 flex items-center justify-center rounded-full border ${
                      isDomainJournal ? 'border-basic-black' : 'border-dark-blue-dark-blue2'
                    }`}
                  >
                    <Arrow
                      className={`${isDomainJournal ? 'text-basic-black' : 'text-dark-blue-dark-blue2'} -rotate-45`}
                    />
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div
          className={`d:pr-76 d:pl-40 flex flex-col gap-y-40 d:border-l ${
            isDomainJournal ? 'd:border-l-basic-gray8' : 'd:border-l-dark-blue-dark-blue6'
          }`}
        >
          <Tabs
            tabs={[
              { id: 'info', label: translate('T_BASIC_INFORMATION') },
              desc && { id: 'desc', label: descriptionTabTitle },
            ]}
            activeTab={isActive}
            className="flex-wrap gap-y-10 d:flex border-b border-basic-black"
            color={routeContext === 'journal-domain' ? 'journalDomain' : 'dark2'}
            onTabChange={(e) => {
              setIsActive(e)
            }}
          />
          {isActive === 'info' ? (
            <JournalInfoGrid ref={infoRef} items={infoItems} className="grid d:grid-cols-3 d:gap-x-40 gap-y-32" />
          ) : isActive === 'desc' && desc ? (
            <div className="w-[74%] flex flex-col gap-y-11" ref={descRef}>
              {descriptionTitle && (
                <Typography variant="h3-bold" component="h2" color="text-basic-black">
                  {descriptionTitle}
                </Typography>
              )}

              <Typography
                variant="body2"
                component="div"
                color="text-basic-black"
                className="text-block-content !text-left [&_*]:!text-left"
                dangerouslySetInnerHTML={{ __html: desc }}
              />
            </div>
          ) : (
            ''
          )}
        </div>
      </div>
      {menuItems.length > 0 && (
        <div
          className={`surface-dark grid gap-y-16 d:grid-cols-2 d:gap-x-32 py-60 ${
            isDomainJournal
              ? 'bg-basic-gray1 shadow-[0_0_0_25vmax_var(--c-basic-gray1)] [clip-path:inset(0_-25vmax)]'
              : 'bg-dark-blue-dark-blue2 full-width-shadow-dark-blue-dark-blue2'
          }`}
        >
          {menuItems.map((link, linkIndex) => {
            const menuLink = getMenuItemLink(link)

            return menuLink ? (
              <Accordion key={`linksOpen-${linkIndex}`} type="block" color="white">
                <Accordion.Item>
                  <Accordion.Trigger variant="only-link" href={menuLink.href}>
                    {menuLink.label}
                  </Accordion.Trigger>
                </Accordion.Item>
              </Accordion>
            ) : null
          })}
        </div>
      )}
      <Keywords tags={tags} locale={locale} journalUuid={page?.journal?.uuid} />
    </header>
  )
}

export default Header
